import { useMemo } from "react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";
import { useAuthContext } from "providers/Auth";
import { crmAnalyticsApi, type DateRangeParams } from "api/crm-analytics";
import type { LeadSource } from "views/leads-id/types";
import { BOOM_LISTING_COLOR, FIVE_MINUTES_MS } from "views/analytics/constants";
import type { SourceColorMap } from "views/analytics/components/LeadPipelineCard";
import {
  moduleDateRangeLabel,
  moduleRangeToDateFilter,
  type ModuleDateRangeValue
} from "views/analytics/components/moduleDateRange";
import { useLeasingDashboardEnabled } from "views/analytics/leasingDashboardFlag";

const BOOM_LISTINGS_SOURCE_NAME = "Boom Listings";

const FALLBACK_PARTNER_CREATED_AT = "2020-01-01";

export const useUnitAnalytics = ({
  unitId,
  dateRange
}: {
  unitId: number;
  dateRange: ModuleDateRangeValue;
}) => {
  const { partner } = useAuthContext();
  const leasingDashboardEnabled = useLeasingDashboardEnabled();

  const isEnabled = Number.isFinite(unitId) && unitId > 0;

  const unitParams = useMemo<DateRangeParams>(
    () => ({ unit_ids: [unitId] }),
    [unitId]
  );

  const dateParams = useMemo<DateRangeParams>(
    () =>
      dateRange.startDate && dateRange.endDate
        ? {
            start_date: moment(dateRange.startDate).startOf("day").format(),
            end_date: moment(dateRange.endDate).endOf("day").format()
          }
        : {},
    [dateRange.startDate, dateRange.endDate]
  );

  const queryParams = useMemo<DateRangeParams>(
    () => ({ ...unitParams, ...dateParams }),
    [unitParams, dateParams]
  );

  const partnerCreatedAt =
    partner?.magic_link?.created_at ||
    moment(FALLBACK_PARTNER_CREATED_AT, "YYYY-MM-DD").toISOString();

  const chartRange = useMemo(
    () => moduleRangeToDateFilter(dateRange, partnerCreatedAt),
    [dateRange, partnerCreatedAt]
  );

  const { data: overview, isLoading: isOverviewLoading } = useQuery({
    queryKey: ["UNIT_ANALYTICS_OVERVIEW", queryParams],
    queryFn: () => crmAnalyticsApi.getOverview(queryParams),
    enabled: isEnabled,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const { data: listingViews, isLoading: isListingViewsLoading } = useQuery({
    queryKey: ["UNIT_ANALYTICS_LISTING_VIEWS", queryParams],
    queryFn: () => crmAnalyticsApi.getListingViews(queryParams),
    enabled: isEnabled,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const { data: leadPipeline, isLoading: isLeadPipelineLoading } = useQuery({
    queryKey: ["UNIT_ANALYTICS_LEAD_PIPELINE", queryParams],
    queryFn: () => crmAnalyticsApi.getLeadPipeline(queryParams),
    enabled: isEnabled && !leasingDashboardEnabled,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const { data: leasingFunnel, isLoading: isLeasingFunnelLoading } = useQuery({
    queryKey: ["UNIT_ANALYTICS_LEASING_FUNNEL", queryParams],
    queryFn: () => crmAnalyticsApi.getLeasingFunnel(queryParams),
    enabled: isEnabled && leasingDashboardEnabled,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const { data: leadJourney, isLoading: isLeadJourneyLoading } = useQuery({
    queryKey: ["UNIT_ANALYTICS_LEAD_JOURNEY", queryParams],
    queryFn: () => crmAnalyticsApi.getLeadJourney(queryParams),
    enabled: isEnabled && leasingDashboardEnabled,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const { data: leadsOverTime, isLoading: isLeadsOverTimeLoading } = useQuery({
    queryKey: ["UNIT_ANALYTICS_LEADS_OVER_TIME", queryParams],
    queryFn: () => crmAnalyticsApi.getLeadsOverTime(queryParams),
    enabled: isEnabled && leasingDashboardEnabled,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const { data: leadsBySource, isLoading: isLeadsBySourceLoading } = useQuery({
    queryKey: ["UNIT_ANALYTICS_LEADS_BY_SOURCE", queryParams],
    queryFn: () => crmAnalyticsApi.getLeadsBySource(queryParams),
    enabled: isEnabled,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const { data: applicationPipeline, isLoading: isApplicationPipelineLoading } =
    useQuery({
      queryKey: ["UNIT_ANALYTICS_APPLICATION_PIPELINE", queryParams],
      queryFn: () => crmAnalyticsApi.getApplicationPipeline(queryParams),
      enabled: isEnabled,
      meta: {
        onError: (err: unknown) => toast.error(getErrorMessage(err))
      }
    });

  const { data: approvalsBySource, isLoading: isApprovalsBySourceLoading } =
    useQuery({
      queryKey: ["UNIT_ANALYTICS_APPROVALS_BY_SOURCE", queryParams],
      queryFn: () => crmAnalyticsApi.getApprovalsBySource(queryParams),
      enabled: isEnabled,
      meta: {
        onError: (err: unknown) => toast.error(getErrorMessage(err))
      }
    });

  const { data: leadSources } = useQuery({
    queryKey: ["UNIT_ANALYTICS_LEAD_SOURCES"],
    queryFn: () =>
      instance
        .get<{ items: LeadSource[] }>("/portal/v1/boom_crm/lead_sources")
        .then((res) => res.data.items),
    enabled: isEnabled && !leasingDashboardEnabled,
    staleTime: FIVE_MINUTES_MS,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const sourceColorMap = useMemo<SourceColorMap>(() => {
    const map: SourceColorMap = new Map();
    leadSources?.forEach((source) => {
      if (source.color) map.set(source.id, source.color);
    });
    leadsBySource?.forEach((source) => {
      if (source.color && !map.has(source.source_id))
        map.set(source.source_id, source.color);
    });
    return map;
  }, [leadSources, leadsBySource]);

  const listingViewsColor = useMemo(
    () =>
      leadsBySource?.find(
        (source) => source.source_name === BOOM_LISTINGS_SOURCE_NAME
      )?.color ?? BOOM_LISTING_COLOR,
    [leadsBySource]
  );

  return {
    leasingDashboardEnabled,
    unitParams,
    dateParams,
    dateLabel: moduleDateRangeLabel(dateRange),
    chartStartDate: chartRange.start_date.toISOString(),
    chartEndDate: chartRange.end_date.toISOString(),
    overview,
    isOverviewLoading,
    listingViews,
    isListingViewsLoading,
    listingViewsColor,
    leadPipeline,
    isLeadPipelineLoading,
    sourceColorMap,
    leasingFunnel,
    isLeasingFunnelLoading,
    leadJourney,
    isLeadJourneyLoading,
    leadsOverTime,
    isLeadsOverTimeLoading,
    leadsBySource,
    isLeadsBySourceLoading,
    applicationPipeline,
    isApplicationPipelineLoading,
    approvalsBySource,
    isApprovalsBySourceLoading
  };
};
